import { defineVizView } from "@/lib/ai/visualization/views/shared/view-definition";
import {
  buildTimeSeriesComposedElement,
  compileTimeSeriesComposedSpec,
  validateTimeSeriesComposedSpec,
} from "@/lib/ai/visualization/views/time-series-composed/logic";
import { timeSeriesComposedAuthoringSchema } from "@/lib/ai/visualization/views/time-series-composed/schema";

/**
 * `time-series-composed` definition の SSOT エントリ。
 */
export const timeSeriesComposedDefinition = defineVizView({
  type: "time-series-composed",
  componentType: "TimeSeriesComposed",
  authoringSchema: timeSeriesComposedAuthoringSchema,
  authoringPrompt: {
    summary:
      "Mixed bars-and-lines time-series for multi-metric stories that need explicit role separation on one timeline.",
    requiredKeys: [
      "xKey",
      "xLabel",
      "lineSeriesKeys",
      "barSeriesKeys",
      "seriesLabelEntries",
    ],
    optionalKeys: ["stacking"],
    selectionRules: [
      "At least one of lineSeriesKeys or barSeriesKeys must contain a metric key.",
      "Choose this view only when bars and lines must play different semantic roles on the same time axis.",
      'Use stacking="grouped" unless additive stacked bars are clearly intended.',
      "Always provide xLabel in task.outputLanguage.",
      "Always provide seriesLabelEntries using selected metric keys only, and cover every selected metric key.",
      "When dataset queryContext requests moving averages and matching fields share the same metricFamily, keep those smoothed series visible, typically in lineSeriesKeys unless another role split is clearly better.",
    ],
    avoidWhen: [
      "Raw audit/detail inspection or simple single-metric trends.",
      "Breakdowns with many grouped series that would overload one combined chart.",
      "Ranking tasks that are better expressed as categorical bars.",
    ],
    minimalExamples: [
      {
        label: "bars-plus-line",
        view: {
          type: "time-series-composed",
          xKey: "date",
          xLabel: "Date",
          lineSeriesKeys: ["ctr"],
          barSeriesKeys: ["clicks"],
          seriesLabelEntries: [
            { key: "clicks", label: "Clicks" },
            { key: "ctr", label: "CTR" },
          ],
        },
      },
    ],
  },
  datasetRequirements: {
    requiresTimeDimension: true,
    requiresCategoryDimension: false,
    minMetricCount: 2,
    requiresCompareMode: false,
  },
  validateSpec: validateTimeSeriesComposedSpec,
  buildElement: buildTimeSeriesComposedElement,
  compileSpec: compileTimeSeriesComposedSpec,
});
